import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// Filters live here rather than in component state so the FilterDrawer and the
// table filter the SAME list. Keyword and filters COMPOSE — they are applied
// independently in useCustomers, so applying both narrows rather than resets.
//
// ⚠️ These filters are applied CLIENT-SIDE, over the pages loaded so far.
// `listCustomers($limit, $nextToken)` takes no filter argument, so the server
// cannot narrow the result set. See the comment in useCustomers.ts.
export type CustomersFilters = Record<string, string>;

let globalFilters: CustomersFilters = {};
const filterListeners: Set<(filters: CustomersFilters) => void> = new Set();

const notifyFilterChange = (filters: CustomersFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetCustomersFilter = () => notifyFilterChange({});

export const useCustomersFilter = () => {
    const [filters, setFilters] = useState<CustomersFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: CustomersFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // The badge on FilterRecordsButton — a filter you cannot see is a filter you
    // forget is on (§7). Empty-string values are treated as unset so a cleared
    // field does not keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
